import React, { useMemo } from "react";
import ReactApexChart from "react-apexcharts";

const DEFAULT_HEIGHT = 350;

const ComponentLifeHistogram = ({
  achievedLives,
  expectedLife,
  bucketSize,
  height = DEFAULT_HEIGHT,
}) => {
  const { categories, counts, expectedBucketIndex } = useMemo(() => {
    const numericExpectedLife = Number(expectedLife);
    const validExpectedLife = Number.isFinite(numericExpectedLife) ? numericExpectedLife : null;
    const validBucketSize = Number.isFinite(bucketSize) && bucketSize > 0 ? bucketSize : 1;

    const numericAchievedLives = Array.isArray(achievedLives)
      ? achievedLives
        .map((life) => Number(life))
        .filter((numericLife) => Number.isFinite(numericLife))
      : [];

    if (numericAchievedLives.length === 0) {
      return { categories: [], counts: [] };
    }

    const minAchievedLife = Math.min(...numericAchievedLives);
    const maxAchievedLife = Math.max(...numericAchievedLives);
    const minRangeValue =
      validExpectedLife != null ? Math.min(minAchievedLife, validExpectedLife) : minAchievedLife;
    const maxRangeValue =
      validExpectedLife != null ? Math.max(maxAchievedLife, validExpectedLife) : maxAchievedLife;

    const getBucketStart = (value) =>
      Math.floor((value - 1) / validBucketSize) * validBucketSize + 1;

    const startingLowerBound = getBucketStart(minRangeValue);
    const endingLowerBound = getBucketStart(maxRangeValue);
    const totalBuckets =
      Math.floor((endingLowerBound - startingLowerBound) / validBucketSize) + 1;
    const categories = Array.from({ length: totalBuckets }, (_, index) => {
      const bucketStart = startingLowerBound + index * validBucketSize;
      const bucketEnd = bucketStart + validBucketSize - 1;
      return `${bucketStart}-${bucketEnd}`;
    });
    const counts = Array(totalBuckets).fill(0);
    const maxInclusiveValue = startingLowerBound + totalBuckets * validBucketSize - 1;

    numericAchievedLives.forEach((numericLife) => {
      if (numericLife < startingLowerBound || numericLife > maxInclusiveValue) return;

      const bucketIndex = Math.floor((numericLife - startingLowerBound) / validBucketSize);
      counts[bucketIndex] += 1;
    });

    const expectedBucketIndex =
      validExpectedLife != null
        ? Math.floor((validExpectedLife - startingLowerBound) / validBucketSize)
        : null;

    return { categories, counts, expectedBucketIndex };
  }, [achievedLives, bucketSize, expectedLife]);

  if (categories.length === 0) {
    return (
      <div className="text-muted ms-16 mt-16">No component life data available for histogram.</div>
    );
  }

  const colors = categories.map((_, index) =>
    index === expectedBucketIndex ? "#F2C94C" : "#008FFB"
  );

  const chartOptions = {
    chart: {
      type: "bar",
      toolbar: { show: false },
    },
    legend: {
      show: false,
    },
    xaxis: {
      categories,
      title: { text: "Interval (hours)" },
    },
    yaxis: {
      title: { text: "Number of Work Orders" },
      forceNiceScale: true,
    },
    dataLabels: { enabled: false },
    tooltip: {
      y: {
        formatter: (value, { dataPointIndex, w }) => {
          const label = w?.globals?.categoryLabels?.[dataPointIndex];
          const count = value ?? 0;
          return label ? `${label}: ${count} Work Orders` : `${count} Work Orders`;
        },
      },
    },
    plotOptions: {
      bar: {
        columnWidth: "55%",
        distributed: true,
      },
    },
    colors,
  };

  const series = [
    {
      name: "Work Orders",
      data: counts,
    },
  ];

  return (
    <ReactApexChart options={chartOptions} series={series} type="bar" height={height} />
  );
};

export default ComponentLifeHistogram;
